/**
 * Platform Event Detail — Admin Dashboard
 *
 * Event detail view with inline edit, Generate Intent Codes button,
 * and embedded IntentCodePanel for QR management.
 *
 * API Endpoints:
 * - GET   /api/v1/admin/events/:id              — Event detail with intent codes
 * - PATCH /api/v1/admin/events/:id              — Update event fields
 * - POST  /api/v1/admin/events/:id/intent-codes — Generate all 3 intent code types
 *
 * @story SCRUM-1470 — 0.1.1.10-02: Platform Events Management Panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';
import IntentCodePanel from './IntentCodePanel';

// ─── Styles ────────────────────────────────────────────────────────

const styles = {
  container: { maxWidth: '900px', margin: '0 auto', padding: '24px' },
  backLink: { display: 'inline-block', marginBottom: '16px', color: '#3b82f6', cursor: 'pointer', fontSize: '14px' },
  title: { fontSize: '22px', fontWeight: '600', margin: '0 0 24px' },
  card: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' },
  fieldRow: { display: 'flex', gap: '16px', marginBottom: '12px', alignItems: 'center' },
  label: { fontWeight: '500', fontSize: '13px', color: '#6b7280', width: '140px', flexShrink: 0 },
  value: { fontSize: '14px', color: '#111827' },
  input: {
    padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', flex: 1,
  },
  btn: {
    padding: '8px 16px', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontWeight: '500', fontSize: '14px',
  },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnSuccess: { backgroundColor: '#10b981', color: '#fff' },
  btnDisabled: { backgroundColor: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600' },
  badgeActive: { backgroundColor: '#dcfce7', color: '#166534' },
  badgeEnded: { backgroundColor: '#f3f4f6', color: '#6b7280' },
  badgeInactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  section: { marginTop: '32px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' },
  toggle: { cursor: 'pointer', fontSize: '14px' },
};

// ─── Component ─────────────────────────────────────────────────────

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [codesKey, setCodesKey] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // 'synced' | 'failed' | null

  const handleNavigate = (_navId, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchEvent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`/api/v1/admin/events/${id}`);
      if (res.success) {
        setEvent(res.data);
        const tz = res.data.timezone || 'America/Denver';
        setEditForm({
          name: res.data.name,
          location_name: res.data.location_name || '',
          location_address: res.data.location_address || '',
          starts_at: res.data.starts_at ? toLocalDatetime(res.data.starts_at, tz) : '',
          ends_at: res.data.ends_at ? toLocalDatetime(res.data.ends_at, tz) : '',
          timezone: tz,
          active: res.data.active,
        });
      }
    } catch {
      // handled by apiRequest
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  const toLocalDatetime = (iso, tz) => {
    if (!iso) { return ''; }
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || 'America/Denver',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = Object.fromEntries(
      formatter.formatToParts(new Date(iso)).map(p => [p.type, p.value])
    );
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };

  const toUTCFromEventTimezone = (localStr, tz) => {
    const [datePart, timePart] = localStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    const guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'America/Denver',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hour12: false,
    });
    const tzParts = formatter.formatToParts(guess);
    const tzHour = parseInt(tzParts.find(p => p.type === 'hour').value);
    const tzDay = parseInt(tzParts.find(p => p.type === 'day').value);

    let offsetHours = tzHour - hour;
    if (tzDay > day) { offsetHours += 24; }
    if (tzDay < day) { offsetHours -= 24; }

    const corrected = new Date(guess.getTime() - offsetHours * 60 * 60 * 1000);

    const verify = new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'America/Denver',
      hour: 'numeric', minute: 'numeric', hour12: false,
    });
    const verifyParts = verify.formatToParts(corrected);
    const verifyHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
    const verifyMinute = parseInt(verifyParts.find(p => p.type === 'minute').value);
    if (verifyHour !== hour || verifyMinute !== minute) {
      const diff = hour - verifyHour;
      return new Date(corrected.getTime() + diff * 60 * 60 * 1000).toISOString();
    }

    return corrected.toISOString();
  };

  const formatDate = (iso, tz) => {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: tz || 'America/Denver',
    });
  };

  const getStatusBadge = () => {
    if (!event.active) {
      return <span style={{ ...styles.badge, ...styles.badgeInactive }}>Inactive</span>;
    }
    if (event.is_past) {
      return <span style={{ ...styles.badge, ...styles.badgeEnded }}>Ended</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgeActive }}>Active</span>;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {};
      if (editForm.name !== event.name) { body.name = editForm.name; }
      if (editForm.location_name !== (event.location_name || '')) { body.location_name = editForm.location_name; }
      if (editForm.location_address !== (event.location_address || '')) {
        body.location_address = editForm.location_address;
        body.google_maps_url = editForm.location_address
          ? `https://maps.google.com/?q=${encodeURIComponent(editForm.location_address)}`
          : null;
      }
      const tz = editForm.timezone || event.timezone || 'America/Denver';
      if (editForm.starts_at && editForm.starts_at !== toLocalDatetime(event.starts_at, tz)) {
        body.starts_at = toUTCFromEventTimezone(editForm.starts_at, tz);
      }
      if (editForm.ends_at && editForm.ends_at !== toLocalDatetime(event.ends_at, tz)) {
        body.ends_at = toUTCFromEventTimezone(editForm.ends_at, tz);
      }
      if (editForm.timezone !== (event.timezone || 'America/Denver')) { body.timezone = editForm.timezone; }
      if (editForm.active !== event.active) { body.active = editForm.active; }

      if (Object.keys(body).length > 0) {
        const patchRes = await apiRequest(`/api/v1/admin/events/${id}`, { method: 'PATCH', body });
        if (patchRes?.data?.hubspot_synced === true) {
          setSyncStatus('synced');
        } else if (patchRes?.data?.hubspot_synced === false) {
          setSyncStatus('failed');
        }
        // Auto-dismiss sync status after 5 seconds
        setTimeout(() => setSyncStatus(null), 5000);
        await fetchEvent();
      }
      setEditing(false);
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCodes = async () => {
    setGenerating(true);
    try {
      await apiRequest(`/api/v1/admin/events/${id}/intent-codes`, { method: 'POST' });
      await fetchEvent();
      setCodesKey((k) => k + 1);
    } catch {
      // handled
    } finally {
      setGenerating(false);
    }
  };

  const allCodesExist = event?.intent_codes?.length >= 6;

  const layoutProps = {
    userRole: 'admin',
    activeNavId: 'events',
    onNavigate: handleNavigate,
    user: {
      name: authUser?.fullName || authUser?.firstName || 'Admin',
      email: authUser?.email || '',
      avatar: authUser?.profilePicture || null
    }
  };

  if (loading) {
    return <DashboardLayout {...layoutProps}><div style={styles.container}><p>Loading event...</p></div></DashboardLayout>;
  }

  if (!event) {
    return <DashboardLayout {...layoutProps}><div style={styles.container}><p>Event not found.</p></div></DashboardLayout>;
  }

  return (
    <DashboardLayout {...layoutProps}>
    <div style={styles.container}>
      <span style={styles.backLink} onClick={() => navigate('/admin/marketing/events')}>
        &larr; Back to Events
      </span>

      <h1 style={styles.title}>{event.name} {getStatusBadge()}</h1>

      <div style={styles.card}>
        {editing ? (
          <>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Name</span>
              <input
                style={styles.input}
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Location Name</span>
              <input
                style={styles.input}
                value={editForm.location_name}
                onChange={(e) => setEditForm((f) => ({ ...f, location_name: e.target.value }))}
              />
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Location Address</span>
              <input
                style={styles.input}
                value={editForm.location_address}
                onChange={(e) => setEditForm((f) => ({ ...f, location_address: e.target.value }))}
              />
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Starts At</span>
              <input
                style={styles.input}
                type="datetime-local"
                value={editForm.starts_at}
                onChange={(e) => setEditForm((f) => ({ ...f, starts_at: e.target.value }))}
              />
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Ends At</span>
              <input
                style={styles.input}
                type="datetime-local"
                value={editForm.ends_at}
                onChange={(e) => setEditForm((f) => ({ ...f, ends_at: e.target.value }))}
              />
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Timezone</span>
              <select
                style={styles.input}
                value={editForm.timezone}
                onChange={(e) => setEditForm((f) => ({ ...f, timezone: e.target.value }))}
              >
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
                <option value="America/Phoenix">Arizona (no DST)</option>
                <option value="America/Anchorage">Alaska (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii (HT)</option>
              </select>
            </div>
            <div style={styles.fieldRow}>
              <span style={styles.label}>Active</span>
              <label style={styles.toggle}>
                <input
                  type="checkbox"
                  checked={editForm.active}
                  onChange={(e) => setEditForm((f) => ({ ...f, active: e.target.checked }))}
                />{' '}
                {editForm.active ? 'Active' : 'Inactive'}
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.fieldRow}><span style={styles.label}>Market</span><span style={styles.value}>{event.market_name}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>HubSpot Event ID</span><span style={styles.value}><code>{event.hubspot_event_id}</code></span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Name</span><span style={styles.value}>{event.name}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Location</span><span style={styles.value}>{event.location_name || '—'}{event.location_address ? `, ${event.location_address}` : ''}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Starts At</span><span style={styles.value}>{formatDate(event.starts_at, event.timezone)}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Ends At</span><span style={styles.value}>{formatDate(event.ends_at, event.timezone)}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Timezone</span><span style={styles.value}>{event.timezone || 'America/Denver'}</span></div>
            <div style={styles.fieldRow}><span style={styles.label}>Capacity</span><span style={styles.value}>{event.capacity || 'Unlimited'}</span></div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => { setEditing(true); setSyncStatus(null); }}>
                Edit
              </button>
              {syncStatus === 'synced' && (
                <span style={{ fontSize: '12px', color: '#166534' }}>&#10003; Saved &middot; Synced to HubSpot</span>
              )}
              {syncStatus === 'failed' && (
                <span style={{ fontSize: '12px', color: '#f59e0b' }}>&#9888; Saved &middot; HubSpot sync failed &mdash; changes saved locally only</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Intent Codes Section */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={styles.sectionTitle}>Intent Codes</h2>
          <button
            style={{ ...styles.btn, ...(allCodesExist || generating ? styles.btnDisabled : styles.btnSuccess) }}
            onClick={handleGenerateCodes}
            disabled={allCodesExist || generating}
          >
            {generating ? 'Generating...' : allCodesExist ? 'All Codes Generated' : 'Generate Intent Codes'}
          </button>
        </div>

        <IntentCodePanel key={codesKey} eventId={id} />
      </div>
    </div>
    </DashboardLayout>
  );
}
